"use client"

import { isMetaRouteEligible } from "@vetpets/event-contracts"
import { usePathname } from "next/navigation"
import { useEffect } from "react"
import { trackMetaTelemetry } from "./meta-event-client"
import { ensureMetaParamBuilderClientReady } from "./meta-param-builder-client"
import type { MetaPixelAdvancedMatchingData } from "./meta-advanced-matching"
import { disableMetaPixelTracking, initializeMetaPixel } from "./meta-pixel-runtime"

type PublicMetaConfig = {
  enabled: boolean
  browser_pixel_enabled: boolean
  capi_enabled: boolean
  pixel_id: string | null
}

function validEnabledConfig(value: unknown): value is PublicMetaConfig {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const config = value as Record<string, unknown>
  const browserPixelEnabled = config.browser_pixel_enabled === true
  const capiEnabled = config.capi_enabled === true
  const pixelIdValid = typeof config.pixel_id === "string" && /^\d{5,32}$/.test(config.pixel_id)
  return config.enabled === true &&
    (browserPixelEnabled || capiEnabled) &&
    (!browserPixelEnabled || pixelIdValid)
}

export default function MetaPixelProvider({
  advancedMatchingData,
}: {
  advancedMatchingData?: MetaPixelAdvancedMatchingData
} = {}) {
  const pathname = usePathname() || "/"
  const advancedMatchingFingerprint = JSON.stringify(advancedMatchingData ?? {})
  const eligible = isMetaRouteEligible(pathname)

  useEffect(() => {
    if (!eligible) {
      disableMetaPixelTracking()
      return
    }

    let cancelled = false
    void ensureMetaParamBuilderClientReady()
      .then(() => fetch(`/api/vetpets/meta/config?path=${encodeURIComponent(pathname)}`, {
        method: "GET",
        cache: "no-store",
        credentials: "same-origin",
      }))
      .then(async (response) => response.ok ? response.json() : null)
      .then((config) => {
        if (cancelled) return
        if (!validEnabledConfig(config)) {
          disableMetaPixelTracking()
          return
        }
        if (config.browser_pixel_enabled) {
          initializeMetaPixel(config.pixel_id!, advancedMatchingData)
        } else {
          disableMetaPixelTracking()
        }
        trackMetaTelemetry(
          { event_name: "PageView", path: pathname },
          { browserPixelEnabled: config.browser_pixel_enabled }
        )
      })
      .catch(() => undefined)

    return () => { cancelled = true }
  }, [advancedMatchingData, advancedMatchingFingerprint, eligible, pathname])

  return null
}
